import {
  ChevronsUpDown,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react';
import { type ElementType, type ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { DEFAULT_MARK_ANIMATION } from '@/components/RapidSOSMark/animations';
import { useCommandPalette } from '@/components/search/CommandPalette';
import {
  AdminAccountMenu,
  SessionAvatar,
  deriveInitials,
} from '@/components/shell/AdminAccountMenu';
import type { AttentionSeverity } from '@/api/adminClient';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useAttention } from '@/hooks/useAttention';
import { useAuth } from '@/hooks/useAuth';
import {
  NAV_ITEMS,
  type NavItem,
  PINNED_NAV_ITEMS,
  badgeLabel,
  groupHeading,
  isActivePath,
  navBadgeCount,
  navItemsFor,
} from '@/lib/adminNav';
import { worstSeverity } from '@/lib/attention';
import { isOrgNavItemActive, orgIdFromPath, orgNavItems } from '@/lib/orgNav';
import { cn } from '@/lib/utils';

function SidebarTooltip({ label, children }: { label: string; children: ReactNode }) {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const showTooltip = (target: EventTarget & HTMLElement) => {
    setAnchorRect(target.getBoundingClientRect());
  };
  const hideTooltip = () => setAnchorRect(null);

  return (
    <div
      className="relative flex justify-center"
      onMouseEnter={(event) => showTooltip(event.currentTarget)}
      onMouseLeave={hideTooltip}
      onFocus={(event) => showTooltip(event.currentTarget)}
      onBlur={hideTooltip}
    >
      {children}
      {anchorRect &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[10000] whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground"
            style={{
              left: anchorRect.right + 12,
              top: anchorRect.top + anchorRect.height / 2,
              transform: 'translateY(-50%)',
            }}
          >
            {label}
          </div>,
          document.body,
        )}
    </div>
  );
}

const SIDEBAR_EXPANDED_STORAGE_KEY = 'rsos.studioAdmin.sidebar.expanded';
const RAIL_TRANSITION_MS = 300;

function readSidebarExpandedPreference(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    if (stored === null) return true;
    return stored === '1';
  } catch {
    return true;
  }
}

function writeSidebarExpandedPreference(expanded: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, expanded ? '1' : '0');
  } catch {
    /* Non-fatal — preference just won't persist this session. */
  }
}

function GroupHeading({ label, first }: { label: string; first: boolean }) {
  return (
    <p className={cn('px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70', first ? 'pt-1' : 'pt-4')}>
      {label}
    </p>
  );
}

type NavBadge = { count: number; severity?: AttentionSeverity };

const BADGE_TONE: Record<AttentionSeverity, string> = {
  critical: 'bg-destructive text-white',
  warning: 'bg-warning-bg text-warning-foreground',
  info: 'bg-muted text-muted-foreground',
};

const DOT_TONE: Record<AttentionSeverity, string> = {
  critical: 'bg-destructive',
  warning: 'bg-warning',
  info: 'bg-muted-foreground',
};

function CountBadge({ badge }: { badge: NavBadge }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
        BADGE_TONE[badge.severity ?? 'warning'],
      )}
    >
      {badgeLabel(badge.count)}
    </span>
  );
}

function badgedLabel(label: string, badge: NavBadge | undefined): string {
  return badge ? `${label}, ${badge.count} need${badge.count === 1 ? 's' : ''} attention` : label;
}

function RailItem({
  label,
  icon: Icon,
  expanded,
  active,
  to,
  badge,
}: {
  label: string;
  icon: ElementType;
  expanded: boolean;
  active: boolean;
  to: string;
  badge?: NavBadge;
}) {
  const link = (
    <Link
      to={to}
      aria-label={badgedLabel(label, badge)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-11 items-center rounded-lg transition-colors',
        expanded ? 'w-full justify-start gap-3 px-3.5' : 'w-full justify-center',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      <span className="relative flex-shrink-0">
        <Icon className="h-5 w-5" />
        {badge && !expanded ? (
          <span
            aria-hidden="true"
            className={cn('absolute -right-1 -top-1 h-2 w-2 rounded-full ring-2 ring-card', DOT_TONE[badge.severity ?? 'warning'])}
          />
        ) : null}
      </span>
      {expanded && <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>}
      {expanded && badge ? <CountBadge badge={badge} /> : null}
    </Link>
  );

  return expanded ? link : <SidebarTooltip label={badgedLabel(label, badge)}>{link}</SidebarTooltip>;
}

/** Opens Studio Admin search; the collapsed rail shows just the icon, named in a tooltip. */
function SearchButton({ expanded, onOpen }: { expanded: boolean; onOpen: () => void }) {
  const { available, shortcut } = useCommandPalette();
  if (!available) return null;
  const button = (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Search"
      aria-keyshortcuts="Meta+K Control+K"
      className={cn(
        'flex h-10 items-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
        expanded ? 'w-full gap-2.5 px-3' : 'w-full justify-center border-transparent',
      )}
    >
      <Search className="h-4 w-4 flex-shrink-0" />
      {expanded ? (
        <>
          <span className="min-w-0 flex-1 truncate text-left text-sm">Search…</span>
          <kbd className="rounded border border-border px-1.5 py-0.5 font-sans text-[10px] font-medium text-muted-foreground">{shortcut}</kbd>
        </>
      ) : null}
    </button>
  );
  return expanded ? button : <SidebarTooltip label={`Search (${shortcut})`}>{button}</SidebarTooltip>;
}

type SidebarNavProps = {
  navItems: NavItem[];
  pinnedItems: NavItem[];
  navLabel: string;
  isActive: (path: string) => boolean;
  badgeFor: (item: NavItem) => NavBadge | undefined;
};

function DesktopSidebar({ navItems, pinnedItems, navLabel, isActive, badgeFor }: SidebarNavProps) {
  const { user } = useAuth();
  const palette = useCommandPalette();
  const [expanded, setExpanded] = useState<boolean>(readSidebarExpandedPreference);
  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    writeSidebarExpandedPreference(next);
  };
  const [contentExpanded, setContentExpanded] = useState(expanded);

  useEffect(() => {
    if (expanded) {
      setContentExpanded(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setContentExpanded(false), RAIL_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-app-hub-sidebar-expanded', expanded ? 'true' : 'false');
    return () => {
      root.removeAttribute('data-app-hub-sidebar-expanded');
    };
  }, [expanded]);

  const widthClass = expanded ? 'w-56' : 'w-16';
  const email = typeof user?.email === 'string' ? user.email : undefined;
  const picture = typeof user?.picture === 'string' ? user.picture : undefined;
  const collapsedIdentityLabel = [
    'Studio Admin',
    email,
    'Account menu',
  ]
    .filter(Boolean)
    .join(' · ');

  const toggleButton = (
    <button
      type="button"
      onClick={toggleExpanded}
      aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      aria-expanded={expanded}
      aria-controls="admin-sidebar-nav"
      title={expanded ? 'Collapse sidebar' : undefined}
      className={cn(
        'flex cursor-pointer items-center rounded-lg transition-colors',
        'text-muted-foreground hover:bg-secondary hover:text-foreground',
        contentExpanded ? 'h-7 w-7 flex-shrink-0 justify-center' : 'h-11 w-full justify-center',
      )}
    >
      {contentExpanded ? (
        <PanelLeftClose className="h-4 w-4 flex-shrink-0" />
      ) : (
        <PanelLeftOpen className="h-5 w-5 flex-shrink-0" />
      )}
    </button>
  );

  return (
    <div
      className={cn(
        'app-hub-desktop-sidebar relative z-[1000] h-full flex-shrink-0 transition-[width] duration-300 ease-out',
        widthClass,
      )}
    >
      <div className={cn('h-full transition-[width] duration-300 ease-out', widthClass)} />

      <div
        className={cn(
          'absolute inset-y-0 left-0 flex h-full flex-col overflow-hidden border-r border-border bg-card transition-[width] duration-300 ease-out',
          widthClass,
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-border pl-[18px] pr-3">
          <RapidSOSMark
            className="h-7 w-7 flex-shrink-0"
            animation={DEFAULT_MARK_ANIMATION}
          />
          {contentExpanded && (
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
              Studio Admin
            </span>
          )}
        </div>

        {palette.available ? (
          <div className="shrink-0 px-2 pt-2">
            <SearchButton expanded={contentExpanded} onOpen={palette.open} />
          </div>
        ) : null}

        <nav
          id="admin-sidebar-nav"
          aria-label={navLabel}
          className="flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden p-2"
        >
          {navItems.map((item, index) => {
            const heading = contentExpanded ? groupHeading(navItems, index) : undefined;
            const showSeparator =
              navItems[index + 1]?.group !== item.group &&
              index < navItems.length - 1 &&
              !(contentExpanded && groupHeading(navItems, index + 1));
            return (
              <div key={item.path}>
                {heading ? <GroupHeading label={heading} first={index === 0} /> : null}
                <RailItem
                  label={item.label}
                  icon={item.icon}
                  expanded={contentExpanded}
                  active={isActive(item.path)}
                  to={item.path}
                  badge={badgeFor(item)}
                />
                {showSeparator && <div className="mx-2 my-2 h-px bg-border" />}
              </div>
            );
          })}
        </nav>

        {pinnedItems.length > 0 ? (
          <div className="flex flex-col gap-1 border-t border-border p-2">
            {pinnedItems.map((item) => (
              <RailItem
                key={item.path}
                label={item.label}
                icon={item.icon}
                expanded={contentExpanded}
                active={isActive(item.path)}
                to={item.path}
              />
            ))}
          </div>
        ) : null}

        <div className="border-t border-border">
          <div className={cn(contentExpanded ? 'px-3 py-3.5' : 'p-2')}>
            {contentExpanded ? (
              <div className="flex flex-col gap-1.5">
                {user ? (
                  <AdminAccountMenu
                    trigger={
                      <button
                        type="button"
                        data-testid="sidebar-session-switch"
                        aria-label="Account menu"
                        className="group -mx-1.5 flex cursor-pointer rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <span className="flex items-center gap-2.5">
                          <SessionAvatar picture={picture} initials={deriveInitials(user)} />
                          <span className="min-w-0 flex-1 leading-tight">
                            <span className="block truncate text-xs font-semibold text-foreground">
                              Studio Admin
                            </span>
                            {email && (
                              <span className="block truncate text-[11px] text-muted-foreground">
                                {email}
                              </span>
                            )}
                          </span>
                          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground" />
                        </span>
                      </button>
                    }
                  />
                ) : null}
                <div className="flex justify-end">{toggleButton}</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                {user && (
                  <SidebarTooltip label={collapsedIdentityLabel}>
                    <AdminAccountMenu
                      trigger={
                        <button
                          type="button"
                          data-testid="sidebar-session-switch"
                          aria-label="Account menu"
                          className="cursor-pointer rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <SessionAvatar picture={picture} initials={deriveInitials(user)} />
                        </button>
                      }
                    />
                  </SidebarTooltip>
                )}
                <SidebarTooltip label="Expand sidebar">{toggleButton}</SidebarTooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileNavigation({ navItems, pinnedItems, navLabel, isActive, badgeFor, currentPath }: SidebarNavProps & { currentPath: string }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const palette = useCommandPalette();
  const email = typeof user?.email === 'string' ? user.email : undefined;
  const picture = typeof user?.picture === 'string' ? user.picture : undefined;

  useEffect(() => {
    setOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <div className="app-hub-mobile-nav relative z-[1000] h-14 shrink-0 items-center justify-between border-b border-border bg-card px-4">
      <Link to="/" aria-label="Studio Admin" className="flex items-center gap-2">
        <RapidSOSMark className="h-7 w-7 flex-shrink-0" animation={DEFAULT_MARK_ANIMATION} />
      </Link>

      <button
        type="button"
        aria-label="Open navigation menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <Menu className="h-5 w-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000] flex w-screen flex-col bg-card text-card-foreground"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <Link
              to="/"
              aria-label="Studio Admin"
              className="flex items-center gap-2"
              onClick={() => setOpen(false)}
            >
              <RapidSOSMark className="h-7 w-7 flex-shrink-0" />
              <span className="text-sm font-semibold">Studio Admin</span>
            </Link>
            <button
              type="button"
              aria-label="Close navigation menu"
              onClick={() => setOpen(false)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav aria-label={navLabel} className="flex-1 overflow-y-auto px-4 py-5">
            {palette.available ? (
              <div className="mb-4">
                <SearchButton
                  expanded
                  onOpen={() => {
                    setOpen(false);
                    palette.open();
                  }}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-2">
              {navItems.map((item, index) => {
                const IconComponent = item.icon;
                const heading = groupHeading(navItems, index);
                const showSeparator =
                  navItems[index + 1]?.group !== item.group &&
                  index < navItems.length - 1 &&
                  !groupHeading(navItems, index + 1);
                const active = isActive(item.path);
                const badge = badgeFor(item);
                return (
                  <div key={item.path}>
                    {heading ? <GroupHeading label={heading} first={index === 0} /> : null}
                    <Link
                      to={item.path}
                      aria-label={badge ? badgedLabel(item.label, badge) : undefined}
                      onClick={() => setOpen(false)}
                      className={cn(
                        'flex h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors',
                        active
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )}
                    >
                      <IconComponent className="h-5 w-5 flex-shrink-0" />
                      <span>{item.label}</span>
                      {badge ? <CountBadge badge={badge} /> : null}
                    </Link>
                    {showSeparator && <div className="my-3 h-px bg-border" />}
                  </div>
                );
              })}
            </div>
          </nav>

          {pinnedItems.length > 0 ? (
            <div className="flex shrink-0 flex-col gap-2 border-t border-border px-4 py-3">
              {pinnedItems.map((item) => {
                const IconComponent = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors',
                      active
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    )}
                  >
                    <IconComponent className="h-5 w-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ) : null}

          <div className="shrink-0 border-t border-border p-4">
            {user && (
              <AdminAccountMenu
                placement="mobile"
                trigger={
                  <button
                    type="button"
                    data-testid="sidebar-session-switch"
                    aria-label="Account menu"
                    className="flex h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    <SessionAvatar picture={picture} initials={deriveInitials(user)} />
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-sm text-foreground">Studio Admin</span>
                      {email && <span className="block truncate text-[11px]">{email}</span>}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0" />
                  </button>
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSidebar() {
  const location = useLocation();
  const access = useAdminAccess();
  const { attention } = useAttention();
  const currentPath = location.pathname;
  const orgId = access.isPlatformAdmin ? orgIdFromPath(currentPath) : undefined;
  const navItems = orgId ? orgNavItems(orgId) : navItemsFor(NAV_ITEMS, access);
  const pinnedItems = navItemsFor(PINNED_NAV_ITEMS, access);
  const navLabel = orgId ? 'Organization navigation' : 'Admin navigation';
  const isActive = (path: string) =>
    orgId && navItems.some((item) => item.path === path)
      ? isOrgNavItemActive(currentPath, orgId, path)
      : isActivePath(currentPath, path);
  const badgeFor = (item: NavItem): NavBadge | undefined => {
    const count = navBadgeCount(item, attention?.counts);
    return count > 0 ? { count, severity: worstSeverity(attention?.items, item.badge) } : undefined;
  };

  return (
    <>
      <MobileNavigation
        navItems={navItems}
        pinnedItems={pinnedItems}
        navLabel={navLabel}
        isActive={isActive}
        badgeFor={badgeFor}
        currentPath={currentPath}
      />
      <DesktopSidebar
        navItems={navItems}
        pinnedItems={pinnedItems}
        navLabel={navLabel}
        isActive={isActive}
        badgeFor={badgeFor}
      />
    </>
  );
}
